// /r/:id — a shared report, for anyone with the link (Oct 15, 2026). Ben: "the link needs to be public and just bring them
// to a report"; links expire after 7 days; it "should show everything the report would show … look identical to the
// report, but without the option to share". So: the report screen's own title bar and sections (ReportContent), seen
// from the sharer's side, with "Shared by … · expires …" where "3 of 10" sits. No Share or Discord, no delete, no
// newer / older, no world bar or resource strip (those need an account). Simulate is for everyone; Attack again (or Open
// market / Rally point) and the profile and map buttons show only to a viewer signed in on that world.
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/api/client";
import { Shell } from "@/kit/chrome";
import { art } from "@/kit/art";
import { StickyBar } from "@/kit/city";
import { Button, Card, Skeleton } from "@/kit/primitives";
import { useSession, worldLabel } from "@/state/session";
import { presenceVillageId, presenceVillages, useInit } from "@/state/queries";
import { fmtDate } from "@/game/time";
import { reportType, simulatorUrl } from "@/game/reports";
import { AttackAgain, REPORT_TITLES, ReportContent, ReportLookProvider } from "./ReportView";
import "../social.css";

export function PublicReport() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const session = useSession();
  const q = useQuery({ queryKey: ["shared-report", id], queryFn: () => api.shares.get(id), staleTime: 60_000, retry: 1 });
  const rep = q.data?.report;
  const expired = q.data && !q.data.ok;

  const signedIn = session.status === "in";
  // signed in and playing that world: the links and the follow-up actions work, from the city they were last in there
  // (or their first city on it)
  const init = useInit(signedIn && !!rep);
  const mine = rep && signedIn ? presenceVillages(init.data?.world, rep.wid).map(presenceVillageId).filter((x): x is number => !!x) : [];
  const vid = !rep || !mine.length ? null : session.wid === rep.wid && session.vid && mine.includes(session.vid) ? session.vid : mine[0];
  const here = !!vid;
  const base = rep ? `/w/${rep.wid}` : "";
  const type = rep ? reportType(rep.b) : "";
  const title = rep ? REPORT_TITLES[type] ?? "Report" : expired ? "Report link" : "Report";
  const world = rep ? worldLabel(rep.wid, rep.wname ? { [String(rep.wid)]: rep.wname } : {}) : "";
  const sim = type === "re_a" || type === "re_d" || type === "re_is";
  const acts = sim || (here && (type === "re_t" || type === "re_s" || type === "re_i"));

  return (
    <Shell className="pg-col pub-report">
      <div className="stack rp-view">
        <div className="tbar rp-tbar">
          <div className="tbar-mid">
            <h1><span className="t">{title}</span></h1>
            {rep && (
              <div className="tbar-sub rp-pos">
                {[`Shared by ${rep.by.l || "a player"}`, ...world.split(" · "), `expires ${fmtDate(rep.exp)}`].map((t, i) => <span key={i} className="nw">{i > 0 && " · "}{t}</span>)}
              </div>
            )}
          </div>
        </div>

        {q.isLoading && <><Skeleton h={210} /><Skeleton h={300} /></>}
        {q.isError && (
          <Card>
            <div className="rp-expired">
              <p>{q.error instanceof Error ? q.error.message : "The report couldn't be loaded."}</p>
              <Button kind="gold" size="md" onClick={() => void q.refetch()}>Try again</Button>
            </div>
          </Card>
        )}
        {expired && (
          <Card>
            <div className="rp-expired">
              <img src={art.social.seal("defeat")} alt="" />
              <p>This report link has expired. Shared reports last 7 days.</p>
              <Button kind="crimson" size="md" onClick={() => nav(signedIn ? "/portal" : "/sign-in")}>Play Valor</Button>
            </div>
          </Card>
        )}

        {rep && (
          <ReportLookProvider value={{ links: here, owner: signedIn && session.uid === rep.by.u }}>
            <ReportContent raw={{ b: rep.b, s: rep.s, ts: Math.floor(rep.ts / 1000) }} uid={rep.by.u} base={base} vid={vid} />
            {acts && <StickyBar className="rp-actions">
              <div className="acts">
                {sim && <Button kind="steel" size="md" onClick={() => window.open(simulatorUrl(rep.b), "_blank", "noopener")}>Simulate</Button>}
                {here && sim && <AttackAgain b={rep.b} base={base} vid={vid} />}
                {here && type === "re_t" && <Button kind="steel" size="md" onClick={() => nav(`${base}/city/${vid}/market`)}>Open market</Button>}
                {here && type === "re_s" && <Button kind="crimson" size="md" onClick={() => nav(`${base}/city/${vid}/rally/troops`)}>Rally point</Button>}
                {here && type === "re_i" && <Button kind="crimson" size="md" onClick={() => nav(`${base}/city/${vid}/rally/movement`)}>Movement</Button>}
              </div>
            </StickyBar>}
          </ReportLookProvider>
        )}
      </div>
    </Shell>
  );
}
